import NoImage from 'assets/images/noimage.png'
import { P } from 'components'
import SvgIcon from 'components/Icon/SvgIcon'
import PMBProjectSelections from 'components/PMBProjectSelections'
import PMBSelect from 'components/PMBSelect'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, AlertTriangle, Zap } from 'react-feather'
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
} from 'reactstrap'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { shakeInvalidFields } from 'utils/shakeInvalidFields'
import {
  useAddGroupInventoryMutation,
  useFetchGroupMappingQuery,
} from './productMappingSchemaListApiSlice'
import { evaluateRule } from 'features/private-views/map-schema/schemaRuleUtils'
import RecipeItem from './RecipeItem'
import RuleGroupedRecipes from './RuleGroupedRecipes'
import { useAutoPopulateAll } from './hooks/useAutoPopulateAll'
import { useSchemaRule, withSchemaUnits } from './hooks/useSchemaRule'
import ModalSuccessGroupMapping from './ModalSuccessGroupMapping'

const ModalGroupMapping = ({
  isOpen,
  toggle,
  onSuccess,
  selectedItems,
  clearSelectedItems,
}) => {
  const { enqueueSnackbar } = useSnackbar()

  const [selectedItem, setSelectedItem] = useState(null)
  const [recipeGroups, setRecipeGroups] = useState([])
  const [isConfirmUnfilledOpen, setIsConfirmUnfilledOpen] = useState(false)
  // Required fields only turn red after a save attempt
  const [showErrors, setShowErrors] = useState(false)
  const [selectedProject, setSelectedProject] = useState(null)
  const [newLotCode, setNewLotCode] = useState('')
  const [hasAutoPopulated, setHasAutoPopulated] = useState(false)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [createdGroupData, setCreatedGroupData] = useState(null)

  const workresultIds = useMemo(() => {
    return Object.values(selectedItems || {})
      .filter((item) => !!item)
      .map((item) => parseInt(item.id, 10))
  }, [selectedItems])

  const { currentData: dataGroupMapping } = useFetchGroupMappingQuery(
    {
      workresultIds,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !isOpen,
    },
  )

  const [addGroupInventory, { isLoading: isLoadingAddGroupInventory }] =
    useAddGroupInventoryMutation()

  // MAPPING POSSIBLE ITEMS LIST
  const possibleItemsList = useMemo(() => {
    return dataGroupMapping?.map((item) => ({
      value: item?.itemId || item?.id,
      label: item?.item?.nameItem || item?.nameItem,
      image: item?.item?.linkFoto || item?.linkFoto || NoImage,
      ...item,
    }))
  }, [dataGroupMapping])

  const inputRef = useRef(null)

  const resetState = () => {
    setSelectedItem(null)
    setRecipeGroups([])
    setSelectedProject(null)
    setNewLotCode('')
    setHasAutoPopulated(false)
    setIsConfirmUnfilledOpen(false)
    setShowErrors(false)
  }

  // Initialize recipeGroups when selectedItem changes
  useEffect(() => {
    const listRecipes = selectedItem?.item?.recipes || selectedItem?.recipes
    if (listRecipes) {
      const initialGroups = listRecipes
        ?.filter((recipe) => !recipe.deleted)
        ?.map((recipe) => ({
          ...recipe,
          id: recipe.itemId || recipe.item?.id || recipe.id,
          itemId: recipe.itemId || recipe.item?.id || recipe.id,
          nameItem: recipe.nameItem || recipe.item?.nameItem || '-',
          unit: recipe.unit || recipe.item?.unit || 'Kg',
          reqAmount: Number(recipe.reqAmount ?? 0),
          image: recipe.linkFoto || recipe.item?.linkFoto || NoImage,
          entries: [
            {
              id: Date.now() + Math.random(),
              inventoryId: null,
              amount:
                Number(recipe.reqAmount ?? 0) *
                Number(selectedItem?.totalQty ?? 1),
            },
          ],
        }))
      // Only set initial if not already auto-populated
      if (!hasAutoPopulated) {
        setRecipeGroups(initialGroups)
      }
    }
  }, [selectedItem, hasAutoPopulated])

  const handleAmountChange = useCallback((recipeIndex, entryId, value) => {
    const replaceValue = value.replace(/[^\d,]/g, '')
    const [integerPart, decimalPart] = replaceValue.split(',')
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    const formattedValue = decimalPart
      ? `${formattedInteger},${decimalPart}`
      : formattedInteger

    const numericValue =
      parseFloat(formattedValue.replace(/\./g, '').replace(',', '.')) || 0

    setRecipeGroups((prev) =>
      prev.map((recipe, rIdx) => {
        if (rIdx !== recipeIndex) return recipe
        return {
          ...recipe,
          entries: (recipe.entries || []).map((entry) =>
            entry.id === entryId ? { ...entry, amount: numericValue } : entry,
          ),
        }
      }),
    )
  }, [])

  const handleInventoryChange = useCallback(
    (recipeIndex, entryId, selectedInventory) => {
      setRecipeGroups((prev) =>
        prev.map((recipe, rIdx) => {
          if (rIdx !== recipeIndex) return recipe
          return {
            ...recipe,
            entries: (recipe.entries || []).map((entry) =>
              entry.id === entryId
                ? { ...entry, inventoryId: selectedInventory }
                : entry,
            ),
          }
        }),
      )
    },
    [],
  )

  const handleAddEntry = useCallback((recipeIndex) => {
    setRecipeGroups((prev) =>
      prev.map((recipe, rIdx) => {
        if (rIdx !== recipeIndex) return recipe
        return {
          ...recipe,
          entries: [
            ...(recipe.entries || []),
            {
              id: Date.now() + Math.random(),
              inventoryId: null,
              amount: 0,
            },
          ],
        }
      }),
    )
  }, [])

  const handleRemoveEntry = useCallback((recipeIndex, entryId) => {
    setRecipeGroups((prev) =>
      prev.map((recipe, rIdx) => {
        if (rIdx !== recipeIndex) return recipe
        return {
          ...recipe,
          entries: (recipe.entries || []).filter((entry) => entry.id !== entryId),
        }
      }),
    )
  }, [])

  const handleSetEntries = useCallback((recipeIndex, newEntries) => {
    setRecipeGroups((prev) =>
      prev.map((recipe, rIdx) => {
        if (rIdx !== recipeIndex) return recipe
        return {
          ...recipe,
          entries: newEntries,
        }
      }),
    )
  }, [])

  const getUsedInventoryIdsForRecipe = useCallback(
    (currentRecipeIndex) => {
      const usedIds = []
      recipeGroups?.forEach((rec, idx) => {
        if (idx !== currentRecipeIndex) {
          rec?.entries?.forEach((entry) => {
            const invId = entry.inventoryId?.id || entry.inventoryId?.value
            if (invId) usedIds.push(invId)
          })
        }
      })
      return usedIds
    },
    [recipeGroups],
  )

  const activeWarehouse = useMemo(() => {
    let warehouse = null
    recipeGroups?.forEach((rec) => {
      rec?.entries?.forEach((entry) => {
        if (entry.inventoryId) {
          const whId =
            entry.inventoryId.warehouseId ??
            entry.inventoryId.customLabel?.warehouseId ??
            entry.inventoryId.warehouse?.id
          const whName =
            entry.inventoryId.warehouseName ??
            entry.inventoryId.customLabel?.warehouseName ??
            entry.inventoryId.warehouse?.name
          if (whId || whName) {
            warehouse = {
              id: whId ? Number(whId) || whId : null,
              name: whName ? String(whName) : null,
            }
          }
        }
      })
    })
    return warehouse
  }, [recipeGroups])



  // Form validation
  const isFormInvalid = useMemo(() => {
    if (!selectedItem) return true
    if (!recipeGroups || recipeGroups.length === 0) return true

    // Must have at least 1 entry where inventory is selected and amount > 0
    const hasAtLeastOneSelected = recipeGroups.some((recipe) =>
      (recipe?.entries || []).some(
        (entry) => entry.inventoryId !== null && Number(entry.amount) > 0,
      ),
    )

    if (!hasAtLeastOneSelected) return true

    // Any entry that has an inventory selected must have a valid amount > 0
    const hasInvalidSelectedEntry = recipeGroups.some((recipe) =>
      (recipe?.entries || []).some(
        (entry) => entry.inventoryId !== null && Number(entry.amount) <= 0,
      ),
    )

    if (hasInvalidSelectedEntry) return true

    // Validate warehouse consistency across all selected inventories
    const allSelectedWarehouseIds = new Set()
    const allSelectedWarehouseNames = new Set()
    recipeGroups.forEach((recipe) => {
      (recipe?.entries || []).forEach((entry) => {
        if (entry.inventoryId && Number(entry.amount) > 0) {
          const whId =
            entry.inventoryId.warehouseId ??
            entry.inventoryId.customLabel?.warehouseId
          const whName =
            entry.inventoryId.warehouseName ??
            entry.inventoryId.customLabel?.warehouseName
          if (whId)
            allSelectedWarehouseIds.add(String(whId).toLowerCase().trim())
          if (whName)
            allSelectedWarehouseNames.add(String(whName).toLowerCase().trim())
        }
      })
    })

    if (allSelectedWarehouseIds.size > 1 || allSelectedWarehouseNames.size > 1) {
      return true
    }

    return false
  }, [selectedItem, recipeGroups])

  const { rule: schemaRule, unitByItemId } = useSchemaRule(
    selectedItem?.itemId || selectedItem?.value,
    (recipeGroups || []).map((r) => r.id),
  )
  // Same order/indexes as recipeGroups, only the unit swapped for the schema's
  const recipesInSchemaUnit = withSchemaUnits(recipeGroups || [], unitByItemId)
  const suppliedItemIds = (recipeGroups || [])
    .filter((r) =>
      (r?.entries || []).some(
        (entry) => entry.inventoryId !== null && Number(entry.amount) > 0,
      ),
    )
    .map((r) => r.id)

  const autoPopulateAll = useAutoPopulateAll({
    rule: schemaRule,
    quantity: Number(selectedItem?.totalQty ?? 1),
    recipes: recipesInSchemaUnit,
    onApply: (groups) => {
      setHasAutoPopulated(true) // keeps the init effect from overwriting the filled entries
      setRecipeGroups(groups)
    },
  })

  const unfilledRecipes = useMemo(() => {
    if (!recipeGroups) return []
    return recipeGroups.filter((recipe) => {
      const entries = recipe?.entries || []
      return !entries.some(
        (entry) => entry.inventoryId !== null && Number(entry.amount) > 0,
      )
    })
  }, [recipeGroups])

  // AUTO FOCUS
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100)
  }, [isOpen])

  // RESET STATE PAYLOAD
  useEffect(() => {
    if (!isOpen) {
      const timeout = setTimeout(() => {
        resetState()
      }, 300)

      return () => clearTimeout(timeout)
    }
  }, [isOpen])

  // AUTO SELECT IF ONLY ONE ITEM
  useEffect(() => {
    if (isOpen && possibleItemsList?.length === 1 && !selectedItem) {
      setSelectedItem(possibleItemsList[0])
    }
  }, [isOpen, possibleItemsList, selectedItem])

  const isProductInvalid = showErrors && !selectedItem

  const handleSubmit = () => {
    const verdict = schemaRule ? evaluateRule(schemaRule, suppliedItemIds) : null
    const missingProduct = !selectedItem
    const missingMaterial = verdict ? !verdict.ok : suppliedItemIds.length === 0
    if (missingProduct || missingMaterial) {
      setShowErrors(true)
      setTimeout(shakeInvalidFields, 50) // wait for the red state to render
      enqueueSnackbar(
        missingProduct
          ? 'Pilih barang jadi terlebih dahulu'
          : verdict?.message || 'Pilih inventori untuk minimal satu material',
        { variant: 'error', preventDuplicate: true },
      )
      return
    }

    if (isFormInvalid) {
      enqueueSnackbar('Minimal satu material harus diisi dengan benar', {
        variant: 'error',
        preventDuplicate: true,
      })
      return
    }

    // With a schema rule, unfilled materials are optional as long as the rule holds, so no "unfilled" confirm
    if (schemaRule) {
      executeSubmit()
      return
    }

    if (unfilledRecipes.length > 0) {
      setIsConfirmUnfilledOpen(true)
      return
    }

    executeSubmit()
  }

  const executeSubmit = async () => {
    try {
      if (isFormInvalid) {
        enqueueSnackbar('Minimal satu material harus diisi dengan benar', {
          variant: 'error',
        })
        return
      }

      // Validate all inventories come from the same warehouse by ID or Name
      const allSelectedWarehouseIds = new Set()
      const allSelectedWarehouseNames = new Set()
      recipeGroups?.forEach((recipe) => {
        (recipe?.entries || []).forEach((entry) => {
          if (entry.inventoryId && Number(entry.amount) > 0) {
            const whId =
              entry.inventoryId.warehouseId ??
              entry.inventoryId.customLabel?.warehouseId
            const whName =
              entry.inventoryId.warehouseName ??
              entry.inventoryId.customLabel?.warehouseName
            if (whId) allSelectedWarehouseIds.add(String(whId).toLowerCase().trim())
            if (whName) allSelectedWarehouseNames.add(String(whName).toLowerCase().trim())
          }
        })
      })

      if (allSelectedWarehouseIds.size > 1 || allSelectedWarehouseNames.size > 1) {
        enqueueSnackbar('Semua material harus berasal dari gudang yang sama.', {
          variant: 'error',
        })
        return
      }

      const allRecipeEntries = recipeGroups?.flatMap((recipe) =>
        (recipe.entries || [])
          ?.filter((entry) => Number(entry.amount) > 0 && entry.inventoryId !== null)
          ?.map((entry) => ({
            inventoryId: entry.inventoryId?.value || entry.inventoryId?.id,
            amount: entry.amount,
          })),
      )

      const payloadPost = {
        workresultIds,
        itemId: selectedItem.itemId || selectedItem.id,
        projectId: selectedProject?.id || null,
        newLotCode: newLotCode || null,
        recipes: allRecipeEntries,
        ...(activeWarehouse?.id && {
          warehouseId: Number(activeWarehouse.id) || activeWarehouse.id,
        }),
      }

      const response = await addGroupInventory(payloadPost)
      console.log('addGroupInventory full response:', response)

      if (!response?.error?.data?.message) {
        enqueueSnackbar('Proses berhasil ditambahkan', {
          variant: 'success',
        })

        const resData = Array.isArray(response?.data?.data)
          ? response?.data?.data[0]
          : Array.isArray(response?.data)
            ? response?.data[0]
            : response?.data?.data || response?.data

        const invGroup =
          response?.data?.inventoryGroup ||
          resData?.inventoryGroup ||
          response?.inventoryGroup

        const createdId =
          invGroup?.id ||
          resData?.id ||
          resData?.groupId ||
          resData?.group_id ||
          resData?.inventoryGroupId ||
          resData?.inventory_group_id ||
          resData?.group?.id ||
          resData?.group?.groupId ||
          resData?.group?.group_id ||
          response?.data?.id ||
          response?.data?.groupId ||
          response?.data?.group_id ||
          response?.data?.inventoryGroupId ||
          response?.data?.inventory_group_id ||
          null

        const createdLabel =
          invGroup?.label ||
          resData?.label ||
          resData?.name ||
          resData?.group?.label ||
          newLotCode ||
          selectedItem?.label ||
          'Grup Baru'

        const groupWarehouseId =
          invGroup?.warehouseId ||
          response?.data?.success?.[0]?.result?.[0]?.warehouseId ||
          activeWarehouse?.id

        const groupWarehouseName =
          activeWarehouse?.name ||
          invGroup?.warehouseName ||
          ''

        const groupWarehouse = groupWarehouseId
          ? {
            id: Number(groupWarehouseId) || groupWarehouseId,
            name: groupWarehouseName,
          }
          : activeWarehouse

        setCreatedGroupData({
          id: createdId,
          label: createdLabel,
          warehouse: groupWarehouse,
        })
        setIsSuccessModalOpen(true)

        clearSelectedItems?.()
        toggle()
        resetState()
        onSuccess()
      } else {
        enqueueSnackbar(
          response?.error?.data?.message ?? 'Inventori gagal ditambahkan.',
          {
            variant: 'error',
          },
        )
      }
    } catch (error) {
      enqueueSnackbar(error?.data?.message ?? 'Inventori gagal ditambahkan', {
        variant: 'error',
      })
    }
  }

  return (
    <>
      <Modal
        centered
        data-testid="product-mapping-schema-modal-group-mapping"
        isOpen={isOpen}
        toggle={toggle}
        autoFocus={false}
        style={{ maxWidth: '800px' }}
        className="modal-dialog-centered"
      >
        <div className="modal-header border-0 pb-3 justify-content-between align-items-center px-4 pt-4">
          <div className="d-flex align-items-center">
            <SvgIcon name={'report-converted'} size={20} className="me-2" color="#101828" />
            <P className="text-xl mb-0" size="large" color="#101828" bold>
              Pemetaan Sekaligus
            </P>
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={toggle}
            aria-label="Close"
          ></button>
        </div>

        <ModalBody className="p-4 pt-3 position-relative">
          {autoPopulateAll.isProcessing && (
            <div
              className="position-absolute w-100 h-100 top-0 start-0 d-flex flex-column align-items-center justify-content-center"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', zIndex: 10 }}
            >
              <Spinner color="primary" />
              <div className="mt-2 text-primary fw-bold">Memproses Auto Populate...</div>
            </div>
          )}
          <div
            className="overflow-y-auto overflow-x-hidden px-1"
            style={{
              maxHeight: 'calc(90vh - 200px)',
              paddingRight: '4px',
            }}
          >
            {/* SECTION 1: Pilih Barang Jadi */}
            <div className="mb-4 pb-4 border-bottom">
              <div className="d-flex align-items-center gap-2 mb-3">
                <div
                  className="d-flex align-items-center justify-content-center bg-primary text-white rounded-circle"
                  style={{
                    width: '24px',
                    height: '24px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    flexShrink: 0,
                  }}
                >
                  1
                </div>
                <P className="text-sm mb-0" size="default" color="#101828" bold>
                  Pilih Barang Jadi
                  <span className="ms-1" style={{ color: '#b42218' }}>
                    *
                  </span>
                </P>
              </div>
              <div data-invalid={isProductInvalid}>
                <PMBSelect
                  ref={inputRef}
                  testId="product-mapping-schema-modal-barang-jadi-select"
                  value={selectedItem}
                  options={possibleItemsList}
                  onChange={(e) => {
                    if (!e || e.value !== selectedItem?.value) {
                      setSelectedItem(e)
                      setRecipeGroups([])
                      setHasAutoPopulated(false)
                    }
                  }}
                  getOptionLabel={(e) => e.label}
                  name="selectedItem"
                  placeholder="Pilih barang jadi..."
                  menuPosition="fixed"
                  menuPlacement="auto"
                  menuPortalTarget={document.body}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderColor: isProductInvalid ? '#dc3545' : '#D0D5DD',
                      borderRadius: '8px',
                      padding: '1px',
                      boxShadow: 'none',
                      minHeight: '42px',
                      '&:hover': { borderColor: isProductInvalid ? '#dc3545' : '#D0D5DD' },
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
              {selectedItem && (
                <div className="mt-2">
                  <span className="text-primary fw-bold" style={{ fontSize: '0.8rem' }}>
                    Total dari {selectedItem?.workresults?.length ?? 0}{' '}
                    pekerjaan ({newFormatThousandSeparator(selectedItem?.totalQty ?? 0, 3)}{' '}
                    {selectedItem?.item?.unit || selectedItem?.unit || 'Kg'})
                  </span>
                </div>
              )}
            </div>

            {/* SECTION 2 & 3: Lot ID & Project */}
            {selectedItem && (
              <div className="mb-4 pb-4 border-bottom">
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div
                        className="d-flex align-items-center justify-content-center bg-primary text-white rounded-circle"
                        style={{
                          width: '24px',
                          height: '24px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                        }}
                      >
                        2
                      </div>
                      <P
                        className="text-sm mb-0"
                        size="default"
                        color="#101828"
                        bold
                      >
                        Lot ID
                        <span className="ms-1 text-muted fw-normal">
                          (opsional)
                        </span>
                      </P>
                    </div>
                    <Input
                      name="newLotCode"
                      data-testid="product-mapping-schema-modal-lot-code-input"
                      value={newLotCode ?? ''}
                      onChange={(e) => setNewLotCode(e.target.value)}
                      placeholder="Masukkan Lot ID"
                      className="form-control text-sm"
                      style={{ borderRadius: '8px', borderColor: '#D0D5DD', height: '42px' }}
                    />
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div
                        className="d-flex align-items-center justify-content-center bg-primary text-white rounded-circle"
                        style={{
                          width: '24px',
                          height: '24px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                        }}
                      >
                        3
                      </div>
                      <P
                        className="text-sm mb-0"
                        size="default"
                        color="#101828"
                        bold
                      >
                        Pilih Project
                        <span className="ms-1 text-muted fw-normal">
                          (opsional)
                        </span>
                      </P>
                    </div>
                    <PMBProjectSelections
                      testId="product-mapping-schema-modal-project-select"
                      placeholder="Cari project..."
                      value={selectedProject}
                      onChange={(project) => setSelectedProject(project)}
                      className="w-100"
                      menuPortalTarget={document.body}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '42px',
                          borderColor: '#D0D5DD',
                          borderRadius: '8px',
                          boxShadow: 'none',
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* SECTION 4: Sumber Material */}
            {selectedItem && recipeGroups.length > 0 && (
              <div className="mb-2">
                <div className="d-flex flex-wrap align-items-start gap-2 mb-3">
                  <div
                    className="d-flex align-items-center justify-content-center bg-primary text-white rounded-circle mt-1"
                    style={{
                      width: '24px',
                      height: '24px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      flexShrink: 0,
                    }}
                  >
                    4
                  </div>
                  <div>
                    <P
                      className="text-sm mb-0"
                      size="default"
                      color="#101828"
                      bold
                    >
                      Sumber Material
                      <span className="ms-1" style={{ color: '#b42218' }}>
                        *
                      </span>
                    </P>
                    <div className="text-xs text-muted">
                      Pastikan setiap material berasal dari gudang yang sama.
                    </div>
                  </div>
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    className="ms-auto d-inline-flex align-items-center gap-1 text-nowrap"
                    style={{ borderRadius: '8px' }}
                    onClick={autoPopulateAll.run}
                    disabled={autoPopulateAll.isProcessing}
                    title="Isi semua material otomatis sesuai aturan skema"
                    data-testid="product-mapping-schema-auto-populate-all-button"
                  >
                    {autoPopulateAll.isProcessing ? <Spinner size="sm" /> : <Zap size={14} />}
                    Auto Populate Semua
                  </Button>
                </div>

                <div className="mt-3">
                  <RuleGroupedRecipes
                    rule={schemaRule}
                    recipes={recipesInSchemaUnit}
                    suppliedItemIds={suppliedItemIds}
                    showErrors={showErrors}
                    quantity={Number(selectedItem?.totalQty ?? 1)}
                    renderRecipe={(recipe, index, overrides) => (
                      <RecipeItem
                        key={`${recipe.id}-${index}`}
                        recipe={recipe}
                        index={index}
                        onAmountChange={handleAmountChange}
                        onInventoryChange={handleInventoryChange}
                        onAddEntry={handleAddEntry}
                        onRemoveEntry={handleRemoveEntry}
                        onSetEntries={handleSetEntries}
                        usedInventoryIds={getUsedInventoryIdsForRecipe(index)}
                        activeWarehouse={activeWarehouse}
                        quantity={Number(selectedItem?.totalQty ?? 1)}
                        {...overrides}
                      />
                    )}
                  />
                </div>
              </div>
            )}
          </div>
        </ModalBody>

        <div className="modal-footer border-top p-3 p-md-4 gap-3">
          <Button
            color="secondary"
            outline
            data-testid="product-mapping-schema-modal-cancel-button"
            className="px-3 border-0 text-muted"
            onClick={toggle}
            style={{ borderRadius: '8px', height: '36px', fontSize: '0.9rem' }}
          >
            Batal
          </Button>
          <Button
            color="primary"
            data-testid="product-mapping-schema-modal-save-button"
            disabled={isLoadingAddGroupInventory}
            onClick={handleSubmit}
            className="px-4 fw-bold flex-grow-1 flex-sm-grow-0"
            style={{ borderRadius: '8px', minWidth: '140px', height: '40px', fontSize: '0.95rem' }}
          >
            {isLoadingAddGroupInventory ? (
              <Spinner size="sm" />
            ) : (
              'Simpan Pemetaan'
            )}
          </Button>
        </div>
      </Modal>

      {/* CONFIRMATION MODAL FOR UNFILLED MATERIALS */}
      <Modal
        isOpen={isConfirmUnfilledOpen}
        toggle={() => setIsConfirmUnfilledOpen(false)}
        centered
        autoFocus={false}
        style={{ maxWidth: '480px' }}
        className="modal-dialog-centered"
      >
        <ModalHeader
          toggle={() => setIsConfirmUnfilledOpen(false)}
          className="border-bottom-0 pb-1 pt-3 px-3 px-md-4"
        >
          <div className="d-flex align-items-center gap-2">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: '38px',
                height: '38px',
                backgroundColor: '#fff7ed',
                border: '1px solid #ffedd5',
              }}
            >
              <AlertTriangle size={20} color="#ea580c" />
            </div>
            <div>
              <h5 className="modal-title fw-bold text-dark mb-0" style={{ fontSize: '1rem' }}>
                Material Belum Lengkap
              </h5>
              <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                Ada {unfilledRecipes.length} material yang belum dipilih inventorinya.
              </span>
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="px-3 px-md-4 py-2">
          <div
            className="p-3 rounded-3 mb-3 d-flex align-items-start gap-2"
            style={{
              backgroundColor: '#fffbeb',
              border: '1px solid #fef3c7',
            }}
          >
            <AlertCircle size={16} color="#d97706" className="mt-1 flex-shrink-0" />
            <div style={{ fontSize: '0.82rem', color: '#92400e', lineHeight: '1.4' }}>
              Material berikut tidak diisi dan tidak akan digunakan dalam proses pemetaan:
            </div>
          </div>

          <div className="d-flex flex-column gap-2 mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {unfilledRecipes.map((recipe, idx) => (
              <div
                key={recipe.id || idx}
                className="p-2 rounded-2 border d-flex justify-content-between align-items-center"
                style={{ backgroundColor: '#f8fafc', fontSize: '0.82rem' }}
              >
                <div className="d-flex align-items-center gap-2">
                  <div className="fw-semibold text-dark">
                    {recipe.nameItem || recipe.label || '-'}
                  </div>
                </div>
                <div className="text-muted small">
                  Dibutuhkan: {newFormatThousandSeparator(recipe.amount || (Number(recipe.reqAmount ?? 0) * Number(selectedItem?.totalQty ?? 1)), 3)} {recipe.unit || 'Kg'}
                </div>
              </div>
            ))}
          </div>

          <div className="text-dark small" style={{ fontSize: '0.82rem' }}>
            Apakah Anda yakin ingin tetap melanjutkan pemetaan?
          </div>
        </ModalBody>

        <div className="modal-footer border-top p-3 gap-2">
          <Button
            color="secondary"
            outline
            className="px-3 border-0 text-muted"
            onClick={() => setIsConfirmUnfilledOpen(false)}
            style={{ borderRadius: '8px', fontSize: '0.85rem' }}
          >
            Periksa Kembali
          </Button>
          <Button
            color="primary"
            disabled={isLoadingAddGroupInventory}
            onClick={() => {
              setIsConfirmUnfilledOpen(false)
              executeSubmit()
            }}
            className="px-3 fw-bold"
            style={{ borderRadius: '8px', fontSize: '0.85rem' }}
          >
            {isLoadingAddGroupInventory ? <Spinner size="sm" /> : 'Tetap Lanjutkan'}
          </Button>
        </div>
      </Modal>

      {autoPopulateAll.confirmModal}

      <ModalSuccessGroupMapping
        isOpen={isSuccessModalOpen}
        toggle={() => {
          setIsSuccessModalOpen(false)
          setCreatedGroupData(null)
          clearSelectedItems?.()
        }}
        groupData={createdGroupData}
        warehouse={createdGroupData?.warehouse || activeWarehouse}
        onStay={() => {
          setIsSuccessModalOpen(false)
          setCreatedGroupData(null)
          clearSelectedItems?.()
        }}
      />
    </>
  )
}

export default ModalGroupMapping
